import {
  formatAddressLines,
  formatMoney,
  getAssetsInfoTotal,
  getAssetsLimitLabel,
  getHouseholdMembersAssetsTotal,
  getTotalIncome
} from "./pages/update-profile-section/update-summary.utils";
import {ActiveUser, MandatoryFlowUser} from "./model/user";
import {getAddressStatus} from "./pages/update-profile-section/update-address-info/update-address-info-form.service";
import {getHouseholdMembersStatus} from "./pages/update-profile-section/update-household-members-info/update-household-members-form.service";

const getAddressSummary = (user: ActiveUser | MandatoryFlowUser): string[] | undefined => {
  const addressInfo = user.addressInfo;

  if (addressInfo?.status !== 'complete') {
    return undefined;
  }

  switch (addressInfo.type) {
    case 'regular':
    case 'nycha':
    case 'shelter':
      return formatAddressLines(addressInfo.currentAddress);
    case 'no-address':
      return formatAddressLines(addressInfo.previousAddress);
  }
};

const getHouseholdMembersSummary = (user: ActiveUser | MandatoryFlowUser): string[] | undefined => {
  const names = user.householdInfo.members
    .map((member) => `${member.firstName} ${member.lastName}`)
    .filter(Boolean);

  if (names.length === 0) {
    return undefined;
  }

  return ['Current members:', names.join(', ')];
};

const getIncomeAssetsSummary = (user: ActiveUser | MandatoryFlowUser): string[] | undefined => {
  if (user.householdInfo.status !== 'complete') {
    return undefined;
  }

  const incomeLine = `Income: ${formatMoney(getTotalIncome(user.householdInfo.income))}`;

  const assetsLine = (() => {
    if (user.householdInfo.type === 'total-assets') {
      return `Assets: ${formatMoney(getAssetsInfoTotal(user.householdInfo.totalAssetsInfo))}`;
    }

    if (user.householdInfo.type === 'specific-assets') {
      return `Assets: ${formatMoney(getHouseholdMembersAssetsTotal(user.householdInfo.members))}`;
    }

    return `Assets: ${getAssetsLimitLabel(user.householdInfo.assetsLimit)}`;
  })();

  return [incomeLine, assetsLine];
};

export const requiredTodoItems: TodoItem[] = [
  {
    initialTitle: 'Add address',
    stepLabel: 'Start adding address info',
    description: 'Where you live',
    link: ['update', 'address-info'],
    duration: '2 min',
    image: '/assets/images/forms/initial/small/address.webp',
    getStatus: getAddressStatus,
    getCompleteSummary: getAddressSummary,
    completeTitle: 'Address',
  },
  {
    initialTitle: 'Add household',
    stepLabel: 'Start adding household members',
    description: `Who you'll live with`,
    link: ['update', 'household-info'],
    duration: '3 min',
    image: '/assets/images/forms/initial/small/members.webp',
    getStatus: getHouseholdMembersStatus,
    getCompleteSummary: getHouseholdMembersSummary,
    completeTitle: 'Household',
  },
  {
    initialTitle: 'Add income',
    stepLabel: 'Start adding income & assets',
    description: 'How much you make',
    link: ['update', 'income-assets-info'],
    duration: '2 min',
    image: '/assets/images/forms/initial/small/income-assets.webp',
    getStatus: (user: ActiveUser | MandatoryFlowUser) => {
      if (user.householdInfo.status === 'complete') {
        if (user.householdInfo.realEstateWithinNycZone) {
          return 'problem'
        }

        return 'complete'
      } else {
        if (getHouseholdMembersStatus(user) === 'complete' || getHouseholdMembersStatus(user) === 'problem') {
          return 'recommended'
        } else {
          return 'not-started'
        }
      }
    },
    getCompleteSummary: getIncomeAssetsSummary,
    completeTitle: 'Income & assets',
  }
]

export interface TodoItem {
  readonly initialTitle: string
  readonly stepLabel: string
  readonly description: string,
  readonly image: string,
  readonly duration: string,
  readonly link: string[],
  readonly getStatus: (user: ActiveUser | MandatoryFlowUser) => 'not-started' | 'recommended' | 'complete' | 'problem',
  readonly getCompleteSummary?: (user: ActiveUser | MandatoryFlowUser) => string[] | undefined,
  readonly completeTitle: string,
}
